import React, { useState, useRef, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

function FileDownloader({ endpoint, pageSize, numberOfPages }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [info, setInfo] = useState(null);

  let endpointRef = useRef(endpoint);
  let pageSizeRef = useRef(pageSize);
  let numberOfPagesRef = useRef(numberOfPages);

  useEffect(() => {
    if (!isDownloading) {
      endpointRef.current = endpoint;
      pageSizeRef.current = pageSize;
      numberOfPagesRef.current = numberOfPages;
    }
  }, [endpoint, pageSize, numberOfPages, isDownloading]);

  const escapeCSVField = (field) => {
    if (field === null || field === undefined) return "";
    let str = String(field);
    str = str.replace(/"/g, '""'); // Escape double quotes
    return `"${str}"`;
  };

  const fetchChunk = async (pageNumber) => {
    try {
      const response = await fetch(`${endpointRef.current}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pageNumber,
          pageSize: pageSizeRef.current,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error en fetch: ${response.statusText}`);
      }

      const json = await response.json();
      return json.result.pageData || [];
    } catch (error) {
      console.error("Fetch error:", error);
      throw error; // Permite que el llamado principal haga retry
    }
  };

  const downloadFile = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    toast.info("Iniciando descarga...", {
          position: "bottom-right",
          autoClose: 4000,
        });

    const headers = [
      "Nombre",
      "Rut",
      "Dv",
      "Sexo",
      "Direccion",
      "Comuna Prime",
      "Codigo Venta",
      "Sexo Abrev",
      "Pueblo Indigena",
      "Pag",
      "Region",
      "Provincia",
      "Comuna",
      "Segmento",
      "Telefono",
      "Email",
    ];

    let csvContent = headers.join(",") + "\n";

    try {
      for (let page = 1; page <= numberOfPagesRef.current; page++) {
        let retries = 3;
        let data = null;

        while (retries > 0) {
          try {
            data = await fetchChunk(page);
            break; // Si salió bien, sal del retry loop
          } catch (error) {
            retries--;
            if (retries === 0) {
              throw new Error(`No se pudo descargar la página ${page}`);
            }
          }
        }

        for (let item of data) {
          const row = [
            escapeCSVField(item.name?.[0]?.name),
            escapeCSVField(item._id),
            escapeCSVField(item.dv?.[0]?.dv),
            escapeCSVField(item.sex?.[0]?.key),
            escapeCSVField(item.address?.[0]?.address),
            escapeCSVField(item.comunaPrime?.[0]?.key),
            escapeCSVField(item.codVenta?.[0]?.key),
            escapeCSVField(item.sexAbrevId === -1 ? "" : item.sexAbrev?.[0]?.key),
            escapeCSVField(item.ethnicity?.[0]?.key),
            escapeCSVField(item.pageId),
            escapeCSVField(item.region?.[0]?.key),
            escapeCSVField(item.province?.[0]?.key),
            escapeCSVField(item.commune?.[0]?.key),
            escapeCSVField(item.segment?.[0]?.key),
            escapeCSVField(
              item.phones ? item.phones.map(p => p.phones.join(" ")).join(" ") : ""
            ),
            escapeCSVField(
              item.emails ? item.emails.map(e => e.emails.join(" ")).join(" ") : ""
            ),
          ];

          csvContent += row.join(",") + "\n";
        }

        setInfo(page);
      }

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "usuarios.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Archivo descargado exitosamente 🎉", {
          position: "bottom-right",
          autoClose: 4000,
        });
    } catch (error) {
      console.error("Error general de descarga:", error);
      toast.error("Error en la descarga 😢", {
          position: "bottom-right",
          autoClose: 5000,
        });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <button
        onClick={downloadFile}
        className={`px-6 py-3 text-white rounded-2xl shadow-md mt-4 transition duration-300
      ${isDownloading ? 'bg-orange-500' : 'bg-blue-600 hover:bg-blue-700'}`}        
        disabled={isDownloading}
      >
        {isDownloading ? `Descargando ${info}/${numberOfPagesRef.current} ` : "Descargar"}
      </button>
      <ToastContainer />
    </div>
  );
}

export default FileDownloader;
